import React, { Fragment, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Button, Row, Col, Form, Table } from 'react-bootstrap';
import classNames from 'classnames';
import moment from 'moment';

import { requestDownload } from 'api/sfms/report';
import {
  requestInform as request,
  requestCalculate,
  requestConfirm
} from 'api/sfms/report/iomc';
import Content from 'components/common-sfims/Content';
import { number2format } from 'function/convert';
import { onEnterKeyPress } from 'function/event';
import { downloadFile } from 'function/file';
import { getText } from 'function/language';
import { getErrorMessage } from 'function/message';
import useBUState from 'global/buState';
import { useConfirmModalState } from 'global/confirmModalState';
import useObjReducer from 'hooks/useObjReducer';
import { RESPONSE_CODE, ROUTER_PATH } from 'types/const';
import isNumeric from 'utils/isNumeric';
import comma from 'utils/format/comma';
import numberFrom from 'utils/number/from';
import format from 'utils/string/format';

/**
 * 리포트 > 장기 IOMC 예측 - 등록/수정/조회
 */
const Inform = () => {
  /* router */
  const history = useHistory();
  const { state } = useLocation();
  const { type, iomcYm, buCode, seq } = useParams();

  /* recoil */
  const { bu } = useBUState();
  const { setConfirmModal } = useConfirmModalState();

  /* state */
  // BU
  const [buName, setBuName] = useState('');
  // 확인 구분 ('calculate': 전체 결과 확인, 'confirm': 최종 확인)
  const [confirmType, setConfirmType] = useState('calculate');
  // 장기 IOMC 예측 리포트 정보
  const [inform, setInform] = useObjReducer({
    rmcList: [],
    iomcList: []
  });
  // 전체 결과 확인 비활성화 여부
  const [isDisableCalculate, setIsDisableCalculate] = useState(false);
  // 최종 확인 비활성화 여부
  const [isDisableConfirm, setIsDisableConfirm] = useState(false);
  // 다운로드 비활성화 여부
  const [isDisableDownload, setIsDisableDownload] = useState(false);
  // 장기 IOMC 예측 리포트 등록/수정 요청 데이터
  const [reportData, setReportData] = useObjReducer({
    iomcYm,
    buCode,
    rmcList: state?.data?.rmcList ?? [],
    iomcList: state?.data?.iomcList ?? []
  });
  // 화면 구분 ('confirm': 확인, 'update': 수정, 'inform': 정보)
  const [viewType, setViewType] = useState(
    state?.data?.viewType ?? (isNumeric(seq) ? 'inform' : seq)
  );

  /* watch */
  useEffect(() => {
    setViewType(state?.data?.viewType ?? (isNumeric(seq) ? 'inform' : seq));
  }, [seq]);
  useEffect(() => {
    setBuName(bu.find((value = {}) => buCode === value.buCode)?.buName ?? '');
  }, [bu]);
  useEffect(() => {
    // 정보 화면인 경우 장기 IOMC 예측 리포트 정보 조회 요청
    if (viewType !== 'confirm') {
      requestInform();
    }
  }, [viewType]);

  /* method */
  // 리포트 다운로드 요청
  const requestDownloadReport = async () => {
    setIsDisableDownload(true);

    try {
      const { data, name } = await requestDownload(seq);

      downloadFile(data, name);
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        message: getErrorMessage(error?.code, error?.error)
      });
    } finally {
      setIsDisableDownload(false);
    }
  };
  // 장기 IOMC 예측 리포트 정보 조회 요청
  const requestInform = async () => {
    try {
      const {
        data = {},
        code,
        message
      } = await request({
        iomcYm,
        buCode
      });

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      const planYm = moment(iomcYm).subtract(1, 'months').format('YYYYMM');

      setInform(data);
      setReportData(
        Object.fromEntries(
          Object.entries(data).map(([key, value = []]) => [
            key,
            value
              .filter((value = {}) => planYm === value.planYm)
              .map((value = {}) => ({
                ...value,
                price: comma(value.price) ?? ''
              }))
          ])
        )
      );
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        message: getErrorMessage(error?.code, error?.error)
      });

      // 목록 화면 표시
      showList();
    }
  };
  // 입력 설정
  const setInput = (type, code, price) => {
    price = number2format(price, ',0.##');

    if (price === false) {
      return;
    }

    const key = `${type}List`;
    const lists = [...reportData[key]];
    const index = lists.findIndex((value = {}) => code === value.code);

    if (index >= 0) {
      lists[index] = {
        ...lists[index],
        price
      };
    } else {
      lists.push({
        planYm: moment(iomcYm).subtract(1, 'months').format('YYYYMM'),
        code,
        price
      });
    }

    setReportData({ [key]: lists });

    if (confirmType === 'confirm') {
      setConfirmType('calculate');
    }
  };
  // 목록 화면 표시
  const showList = () => {
    history.push(format(ROUTER_PATH.sfms.report.list, { type }), state);
  };

  /* event */
  // 전체 결과 확인 클릭 시
  const onCalculateClick = async () => {
    if (isDisableCalculate) {
      return;
    }

    setIsDisableCalculate(true);

    try {
      const {
        data = {},
        code,
        message
      } = await requestCalculate({
        ...reportData,
        rmcList: reportData.rmcList.map((value = {}) => ({
          ...value,
          price: numberFrom(value.price.replaceAll(',', ''), 0)
        })),
        iomcList: reportData.iomcList.map((value = {}) => ({
          ...value,
          price: numberFrom(value.price.replaceAll(',', ''), 0)
        }))
      });

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      setInform(data);
      setConfirmType('confirm');
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        message: getErrorMessage(error?.code, error?.error)
      });
    } finally {
      setIsDisableCalculate(false);
    }
  };
  // 최종 확인 클릭 시
  const onConfirmClick = async () => {
    setIsDisableConfirm(true);

    try {
      const { code, message } = await requestConfirm({
        iomcYm,
        buCode
      });

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      // 목록 화면 표시
      showList();
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        message: getErrorMessage(error?.code, error?.error)
      });
      setIsDisableConfirm(false);
    }
  };
  // 설정
  const onSubmit = () => {
    if (confirmType === 'calculate') {
      onCalculateClick();

      return;
    }

    onConfirmClick();
  };

  /* view */
  return (
    <Content>
      {viewType !== 'inform' && (
        <Fragment key="title">
          {getText(viewType === 'confirm' ? 'insert' : viewType)}
        </Fragment>
      )}
      <Fragment key="body">
        <Row className="align-items-center g-2 mb-3">
          <Col>
            <h6 className="fs-0 mb-0">
              {format(getText('reportIOMCInform'), {
                ym: moment(iomcYm).format(
                  format(getText('ym'), {
                    year: 'YYYY',
                    month: 'MM'
                  })
                ),
                buName
              })}
            </h6>
          </Col>
          {viewType === 'inform' && (
            <Col xs="auto">
              <Button size="sm" onClick={() => setViewType('update')}>
                {getText('update')}
              </Button>
            </Col>
          )}
        </Row>
        <Table bordered responsive className="fs--1 text-center mb-3">
          <tbody className="align-middle">
            {['rmc', 'iomc'].map((type, index) => (
              <Fragment key={`inform.${type}`}>
                <tr>
                  {index === 0 && (
                    <td className="bg-200 text-700 border-y border-300">
                      {getText('buName')}
                    </td>
                  )}
                  <td className="bg-200 text-700 border-y border-300">
                    {moment(iomcYm).format(
                      format(getText('monthNumber'), { month: 'M' })
                    )}
                  </td>
                  {new Array(7).fill(-1).map((start, index) => (
                    <td
                      key={`month${start + index}`}
                      className="bg-200 text-700 border-y border-300"
                    >
                      {moment(iomcYm)
                        .add(start + index, 'months')
                        .format(format(getText('monthNumber'), { month: 'M' }))}
                    </td>
                  ))}
                </tr>
                <tr>
                  {index === 0 && (
                    <td
                      className="bg-200 text-700 border-y border-300"
                      rowSpan={21}
                    >
                      {buName}
                    </td>
                  )}
                  <td className="bg-200 text-700 border-y border-300">Item</td>
                  <td className="bg-200 text-700 border-y border-300">
                    {getText('performance')}
                  </td>
                  {new Array(6).fill(getText('plan')).map((value, index) => (
                    <td
                      key={`plan${index}`}
                      className="bg-200 text-700 border-y border-300"
                    >
                      {value}
                    </td>
                  ))}
                </tr>
                {[
                  'RMC',
                  '1F010',
                  '1F020',
                  '1F030',
                  '1F040',
                  '1F050',
                  '1F070',
                  '1F090',
                  '1F080'
                ].map(code => (
                  <tr key={`inform.${type}.${code}`}>
                    <td className="bg-200 text-700 border-y border-300">
                      {getText(code === 'RMC' ? type : `species${code}`)}
                    </td>
                    {new Array(7).fill(-1).map((start, index) => (
                      <td
                        key={`inform.${type}.${code}-${start + index}`}
                        className={classNames({
                          'text-end': index !== 0 || viewType === 'inform'
                        })}
                      >
                        {index === 0 && viewType !== 'inform' ? (
                          <Form.Control
                            size="sm"
                            type="text"
                            value={
                              reportData[`${type}List`].find(
                                (value = {}) => code === value.code
                              )?.price ?? ''
                            }
                            autoFocus={type === 'rmc' && code === 'RMC'}
                            className="text-end"
                            placeholder={getText('inputNumber')}
                            onChange={({ target: { value } }) => {
                              setInput(type, code, value);
                            }}
                            onKeyUp={event => onEnterKeyPress(event, onSubmit)}
                          />
                        ) : inform[`${type}List`].find(
                            (value = {}) =>
                              code === value.code &&
                              moment(iomcYm)
                                .subtract(1, 'months')
                                .isSame(value.planYm, 'month')
                          ) ? (
                          comma(
                            inform[`${type}List`].find(
                              (value = {}) =>
                                code === value.code &&
                                moment(iomcYm)
                                  .add(start + index, 'months')
                                  .isSame(value.planYm, 'month')
                            )?.price
                          ) || 'ㅡ'
                        ) : (
                          'ㅡ'
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </Table>
      </Fragment>
      <Fragment key="footerButton">
        {viewType === 'inform' ? (
          <>
            <Button
              disabled={isDisableDownload}
              size="sm"
              variant="secondary"
              className="ms-auto"
              onClick={requestDownloadReport}
            >
              {getText('downloadExcel')}
            </Button>
            <Button size="sm" className="ms-2" onClick={showList}>
              {getText('list')}
            </Button>
          </>
        ) : (
          <>
            <Button
              size="sm"
              variant="secondary"
              className="ms-auto"
              onClick={showList}
            >
              {getText('cancel')}
            </Button>
            <Button
              disabled={confirmType !== 'calculate' || isDisableCalculate}
              size="sm"
              className="ms-2"
              onClick={onCalculateClick}
            >
              {getText('calculate')}
            </Button>
            <Button
              disabled={confirmType !== 'confirm' || isDisableConfirm}
              size="sm"
              className="ms-2"
              onClick={onConfirmClick}
            >
              {getText('confirm')}
            </Button>
          </>
        )}
      </Fragment>
    </Content>
  );
};

export default Inform;
